import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const SLOTS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export default function Warranty() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [original, setOriginal] = useState(null);
  const [warranty, setWarranty] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slot, setSlot] = useState("");

  const dateStr = useMemo(() => {
    if (!date) return "";
    const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0"); const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [date]);

  const isToday = useMemo(() => {
    if (!date) return false;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    return d.getTime() === t.getTime();
  }, [date]);

  const isSlotPast = (s) => {
    if (!isToday) return false;
    const [hh, mm] = s.split(":").map(Number);
    const st = new Date(); st.setHours(hh, mm, 0, 0);
    return st.getTime() <= Date.now() + 15 * 60 * 1000;
  };

  const check = async (e) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) return;
    setChecking(true);
    setOriginal(null); setWarranty(null);
    try {
      const { data } = await api.get(`/bookings/track/${code.trim().toUpperCase()}`);
      if (data.is_warranty_return) {
        toast.error("That ticket is itself a warranty return. Use the original ticket.");
      } else if (!data.warranty.eligible) {
        toast.error("Repair not complete yet — warranty return not available.");
      } else if (!data.warranty.active) {
        toast.error(`Warranty expired on ${data.warranty.expires_at?.slice(0, 10)}.`);
      } else {
        setOriginal(data);
        setWarranty(data.warranty);
      }
    } catch {
      toast.error("Ticket not found");
    } finally {
      setChecking(false);
    }
  };

  const submit = async () => {
    if (!dateStr || !slot) { toast.error("Pick a drop-off day and time."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/warranty-rebook", {
        ticket_code: code.trim().toUpperCase(),
        email: email.trim(),
        dropoff_date: dateStr,
        dropoff_time: slot,
        notes,
      });
      navigate(`/confirmation/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Warranty rebook failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="overline flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> 90-day warranty return</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Something not right? Bring it back.</h1>
        <p className="mt-2 text-slate-600">Every repair is covered for 90 days. Verify your ticket and pick a drop-off — no payment, and you'll jump the queue.</p>
      </div>

      {!original && (
        <form onSubmit={check} className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Ticket code</label>
            <input
              data-testid="warranty-ticket-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="RN-XXXXXXXX"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email on the original booking</label>
            <input
              data-testid="warranty-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <button
            data-testid="warranty-check-btn"
            disabled={checking}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40"
          >
            {checking ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : <>Check warranty <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      )}

      {original && (
        <div className="space-y-6" data-testid="warranty-schedule-block">
          <div className="p-6 rounded-2xl border border-emerald-200 bg-emerald-50">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Warranty active</div>
                <div className="mt-1 font-mono font-bold text-slate-900">{original.ticket_code}</div>
                <div className="mt-1 text-sm text-slate-700">{original.brand} {original.model_name}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Days left</div>
                <div className="font-mono text-2xl font-bold text-emerald-900" data-testid="warranty-days-left">{warranty.days_left}</div>
                <div className="text-xs text-emerald-700">expires {warranty.expires_at?.slice(0, 10)}</div>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="overline mb-3">Drop-off date</div>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const t = new Date(); t.setHours(0, 0, 0, 0);
                  return d < t;
                }}
              />
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="overline mb-3">Drop-off time slot</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SLOTS.map((s) => {
                const past = isSlotPast(s);
                const active = slot === s;
                return (
                  <button
                    key={s}
                    data-testid={`warranty-slot-${s}`}
                    onClick={() => !past && setSlot(s)}
                    disabled={past}
                    className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition ${
                      past ? "border-slate-200 bg-slate-50 text-slate-400 line-through cursor-not-allowed"
                        : active ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <label className="block">
              <span className="block overline mb-2">What's wrong this time? (optional)</span>
              <textarea
                data-testid="warranty-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="e.g. Screen has developed a bright spot in the top-left corner…"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900"
              />
            </label>
          </div>

          <button
            data-testid="warranty-submit-btn"
            onClick={submit}
            disabled={submitting || !dateStr || !slot}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-500 disabled:opacity-40"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Booking…</> : <>Confirm warranty return — no charge <ArrowRight className="w-4 h-4" /></>}
          </button>
        </div>
      )}
    </div>
  );
}
